import React, { useEffect, useRef } from 'react';
import CanonicalTag from '../components/CanonicalTag';

const WHY_CARDS = [
  { icon: '📈', title: 'Professional Growth', text: 'Access to continuous learning opportunities, skill development programs, and exposure to diverse projects that enhance your professional capabilities and career trajectory.' },
  { icon: '🚀', title: 'Innovative Projects', text: 'Work on cutting-edge infrastructure and technology initiatives that challenge conventional approaches and allow you to apply the latest tools and methodologies.' },
  { icon: '🤝', title: 'Collaborative Culture', text: 'Be part of a team-oriented environment where knowledge sharing, mutual respect, and collective problem-solving drive success and create a positive workplace atmosphere.' },
  { icon: '💰', title: 'Competitive Compensation', text: 'Receive fair, market-competitive remuneration packages that recognize your skills, experience, and contributions to organizational success.' },
  { icon: '⚖️', title: 'Work-Life Balance', text: 'Enjoy policies and practices that support healthy work-life integration, understanding that personal well-being contributes to professional excellence.' },
  { icon: '🏆', title: 'Recognition and Rewards', text: 'Performance-based recognition systems that acknowledge outstanding contributions and provide pathways for advancement and increased responsibilities.' },
];

function WhyWorkCards() {
  const gridRef = useRef(null);

  useEffect(() => {
    const cards = gridRef.current?.querySelectorAll('.why-card');
    if (!cards) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('why-card--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="why-cards-grid" ref={gridRef}>
      {WHY_CARDS.map((card, i) => (
        <div className="why-card" key={card.title} style={{ '--delay': `${i * 0.1}s` }}>
          <div className="why-card-icon">{card.icon}</div>
          <div className="why-card-accent" />
          <h3>{card.title}</h3>
          <p>{card.text}</p>
        </div>
      ))}
    </div>
  );
}

function Careers() {
  return (
    <div>
      <CanonicalTag />
      <div className="page-header">
        <div className="container">
          <h1>Careers at Udugiri Infratech</h1>
          <p>Join our team of innovators and professionals</p>
        </div>
      </div>

      <section className="page-section">
        <div className="container">
          <div className="content-section">
            <span className="section-label">Join Us</span>
            <h2>Build Your Career With Us</h2>
            <p>
              At Udugiri Infratech, we recognize that our greatest asset is our people. We are constantly seeking
              talented, motivated individuals who share our passion for excellence and innovation in infrastructure
              and technology services. If you're looking for a career that offers professional growth, challenging
              projects, and the opportunity to make a meaningful impact, we invite you to explore opportunities
              with us.
            </p>
            <p>
              Our organization provides a dynamic work environment where creativity is encouraged, expertise is
              valued, and contributions are recognized. We believe in nurturing talent, fostering collaboration,
              and creating pathways for career advancement that align with both organizational goals and individual
              aspirations.
            </p>
          </div>

          <div className="content-section">
            <span className="section-label">Perks &amp; Benefits</span>
            <h2>Why Work With Udugiri Infratech?</h2>
            <WhyWorkCards />
          </div>

          <div className="content-section">
            <h2>Our Work Culture</h2>
            <p>
              Udugiri Infratech fosters a culture built on core values of integrity, excellence, innovation, and
              collaboration. We believe in:
            </p>
            <ul>
              <li><strong>Open Communication:</strong> Transparent dialogue across all organizational levels, encouraging ideas and feedback</li>
              <li><strong>Continuous Learning:</strong> Investment in training, certifications, and knowledge enhancement programs</li>
              <li><strong>Diversity and Inclusion:</strong> Welcoming talent from diverse backgrounds and perspectives</li>
              <li><strong>Innovation Encouragement:</strong> Supporting creative thinking and new approaches to problem-solving</li>
              <li><strong>Ethical Practices:</strong> Maintaining highest standards of professional conduct and business ethics</li>
              <li><strong>Team Empowerment:</strong> Providing autonomy and trust to teams for decision-making and execution</li>
            </ul>
          </div>

          <div className="content-section">
            <h2>Current Opportunities</h2>
            <p>
              We regularly seek qualified professionals across various disciplines. Current and upcoming opportunities
              include positions in:
            </p>

            <h3>Surveying and Geomatics</h3>
            <ul>
              <li>Licensed Land Surveyors</li>
              <li>Survey Technicians and Field Engineers</li>
              <li>GPS/GNSS Specialists</li>
              <li>Hydrographic Survey Engineers</li>
              <li>CAD Operators and Drafters</li>
            </ul>

            <h3>GIS and Remote Sensing</h3>
            <ul>
              <li>GIS Analysts and Specialists</li>
              <li>Remote Sensing Experts</li>
              <li>Spatial Database Administrators</li>
              <li>GIS Application Developers</li>
              <li>Cartographers and Map Designers</li>
            </ul>

            <h3>Civil Engineering</h3>
            <ul>
              <li>Civil Engineers (Highway, Water Resources, Structural)</li>
              <li>Project Managers and Coordinators</li>
              <li>Design Engineers</li>
              <li>Quality Control Engineers</li>
              <li>Site Supervisors</li>
            </ul>

            <h3>Support Functions</h3>
            <ul>
              <li>Business Development Executives</li>
              <li>Project Coordinators</li>
              <li>Administrative Staff</li>
              <li>Accounts and Finance Professionals</li>
            </ul>
          </div>

          <div className="content-section">
            <h2>Qualifications We Seek</h2>
            <p>
              While specific requirements vary by position, we generally look for candidates who demonstrate:
            </p>
            <ul>
              <li>Relevant educational qualifications (Diploma, Bachelor's, or Master's degree in related fields)</li>
              <li>Professional certifications and licenses where applicable</li>
              <li>Practical experience in relevant domains (experience levels vary by position)</li>
              <li>Proficiency with industry-standard software and tools</li>
              <li>Strong analytical and problem-solving abilities</li>
              <li>Excellent communication and interpersonal skills</li>
              <li>Ability to work both independently and collaboratively</li>
              <li>Commitment to quality and attention to detail</li>
              <li>Adaptability and willingness to learn new technologies</li>
            </ul>
          </div>

          <div className="content-section">
            <h2>Application Process</h2>
            <p>
              If you're interested in joining Udugiri Infratech, please follow these steps:
            </p>
            <ul>
              <li><strong>Review Opportunities:</strong> Identify positions that match your qualifications and career interests</li>
              <li><strong>Prepare Application:</strong> Update your resume highlighting relevant experience, skills, and achievements</li>
              <li><strong>Submit Application:</strong> Send your resume and cover letter to careers@oitech.co.in with the position title in the subject line</li>
              <li><strong>Initial Screening:</strong> Our HR team will review applications and contact shortlisted candidates</li>
              <li><strong>Interview Process:</strong> Selected candidates will participate in technical and HR interviews</li>
              <li><strong>Offer and Onboarding:</strong> Successful candidates receive offer letters and join our comprehensive onboarding program</li>
            </ul>
          </div>

          <div className="content-section">
            <h2>Internship and Training Programs</h2>
            <p>
              We also offer internship opportunities for students and recent graduates seeking practical experience
              in surveying, GIS, and civil engineering. Our internship programs provide:
            </p>
            <ul>
              <li>Hands-on experience with real projects and industry-standard tools</li>
              <li>Mentorship from experienced professionals</li>
              <li>Exposure to professional work environments and practices</li>
              <li>Potential pathways to full-time employment for outstanding performers</li>
            </ul>
          </div>

          <div className="content-section">
            <h2>Get in Touch</h2>
            <p>
              For career-related inquiries, please contact us at:
            </p>
            <ul>
              <li><strong>Email:</strong> careers@oitech.co.in</li>
              <li><strong>Subject Line:</strong> Include the position you're interested in or "General Career Inquiry"</li>
              <li><strong>Attachments:</strong> Resume/CV in PDF format, cover letter (optional but recommended)</li>
            </ul>
            <p>
              We review applications on an ongoing basis and maintain a database of qualified candidates for future
              opportunities. Even if there isn't a current opening that matches your profile, we encourage you to
              submit your resume for consideration when suitable positions become available.
            </p>
          </div>

          <div className="content-section">
            <h2>Join Us in Shaping the Future</h2>
            <p>
              At Udugiri Infratech, every team member plays a vital role in our success and growth. We're not just
              offering jobs; we're providing careers where you can develop your skills, contribute to meaningful
              projects, and be part of an organization that values your potential. If you're ready to take the next
              step in your professional journey, we look forward to hearing from you.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Careers;
